/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import { TENANT_APEX } from '@aglyn/aglyn/app-utils/host-naming'
import { useUser } from '@aglyn/tenant-feature-instance'
import { authorizedFetch } from '@aglyn/shared-util-http/authorized-token'
import { useEffect, useRef } from 'react'
import { editorHintCookieDomain } from './editor-hint-cookie.component'
import { emailGateWouldRefuse } from '../utils/email-verification-gate'
import {
  isSignUpLandingHeld,
  markLeavingConsole,
  useSignUpLandingHeld,
} from '../utils/sign-up-landing-hold'

/**
 * The `*.aglyn.app` half of the editor-presence hint (AGL-1842).
 *
 * `EditorHintCookie` covers the hosts that are same-site with the console —
 * `Domain=.aglyn.com`/`.aglyn.io` — but `*.aglyn.app`, where every platform
 * subdomain site lives, is a DIFFERENT registrable domain, and no cookie set
 * from here can reach it. The one channel that can is a top-level
 * navigation: this component, once per {@link EDIT_HINT_BOUNCE_INTERVAL_MS}
 * per browser while signed in, fetches a seconds-lived signed blob from
 * `/api/edit-hint/blob` and bounces the browser through the tenant app's
 * `console.aglyn.app/api/edit-hint/set`, which plants the hint cookies on
 * `.aglyn.app` first-party and sends the browser straight back here.
 *
 * The cost is one redirect flash, paid only by signed-in console editors —
 * an anonymous site visitor never sees any of this machinery. Guards, each
 * of which must hold before anything moves:
 *
 * - auth resolved to a real user (an unknown verdict neither bounces nor
 *   clears the throttle);
 * - top-level window — the `/edit-access` silent probe embeds console pages
 *   in an iframe, where a navigation would kill the probe and the cookie
 *   write would be partitioned into uselessness anyway;
 * - not the `/edit-access` page itself — that page is mid-handshake with a
 *   tenant site (popup or probe) and must never be navigated away;
 * - not a sign-in door, and not while a sign-up is landing — the door is
 *   still awaiting the acquisition record and the workspace when the user
 *   resolves, and this navigation tore it down mid-flight on a slow phone
 *   (AGL-3578's shape through a second door). The doors always navigate on,
 *   so the bounce runs on the page they land;
 * - a first-party console host (`editorHintCookieDomain` non-null) — a
 *   white-label console on a customer's domain must not leak its editors
 *   through our bounce, and localhost has no `.aglyn.app` to reach;
 * - the throttle stamp is stale, and STORABLE: the stamp is written before
 *   navigating, so a browser that refuses storage skips the bounce rather
 *   than looping through it on every load;
 * - the session passes the email-verification gate (AGL-479), which the blob
 *   mint applies — see the note at that guard for why it precedes the stamp.
 *
 * Sign-out clears the stamp so the next sign-in re-plants promptly. It
 * cannot clear the `.aglyn.app` cookies from here — that boundary again —
 * so a signed-out editor's hint dies by expiry, and sooner than that by the
 * exchange failing closed server-side (removed membership, disabled
 * account). A stale hint costs one refused, invisible POST.
 */

/** The reserved tenant-app host the bounce lands on. `console` is in
 * `RESERVED_SUBDOMAINS` (host-naming.ts), so no customer can ever own it;
 * `/api/*` is outside the tenant middleware's matcher, so the endpoint
 * serves there no matter how tenant resolution treats the hostname.
 *
 * Derived from the configured tenant apex (AGL-2176). It was the literal
 * `https://console.aglyn.app`, and this is a deliberate TOP-LEVEL navigation
 * once a day per browser — so on a self-host install it took the operator's
 * editor to Aglyn's domain, to plant a cookie on an apex the operator does not
 * control and the browser would refuse anyway. Both halves wrong: it cannot
 * work, and it leaves. */
export const EDIT_HINT_BOUNCE_ORIGIN = `https://console.${TENANT_APEX}`

export const EDIT_HINT_BOUNCE_STAMP_KEY = 'aglyn-edit-hint-bounce-at'

/** The pages that create the session and still have work to do once it
 * exists. Leaving one mid-flight loses that work. */
export const EDIT_HINT_BOUNCE_SKIPPED_PATHS: ReadonlySet<string> = new Set([
  '/signin',
  '/signup',
])

/** Once a day per browser; the planted cookie lives 7, so a weekly console
 * visit keeps the hint alive continuously. */
export const EDIT_HINT_BOUNCE_INTERVAL_MS = 24 * 60 * 60 * 1000

export interface EditHintBounceProps {
  /** Test seam: performs the top-level navigation. */
  navigate?: (url: string) => void
}

export default function EditHintBounce({
  navigate,
}: EditHintBounceProps): null {
  const { data: user } = useUser()
  const signUpLanding = useSignUpLandingHeld()
  // One attempt per mount — auth state re-emits must not re-fire the work.
  const startedRef = useRef(false)

  useEffect(() => {
    if (user === undefined) return
    if (user === null) {
      // Signed out: drop the throttle so the NEXT sign-in bounces at once
      // instead of waiting out a stamp from the previous session.
      try {
        window.localStorage.removeItem(EDIT_HINT_BOUNCE_STAMP_KEY)
      } catch {
        // Nothing to clear where nothing could be stored.
      }
      return
    }
    if (startedRef.current) return
    if (window.top !== window) return
    if (window.location.pathname === '/edit-access') return
    if (EDIT_HINT_BOUNCE_SKIPPED_PATHS.has(window.location.pathname)) return
    if (signUpLanding) return
    if (!editorHintCookieDomain(window.location.hostname)) return

    let lastBounceAt: number
    try {
      lastBounceAt =
        Number(window.localStorage.getItem(EDIT_HINT_BOUNCE_STAMP_KEY)) || 0
    } catch {
      // Unreadable storage means an unwritable stamp: bounce nothing, or
      // every console load becomes a redirect.
      return
    }
    if (Date.now() - lastBounceAt < EDIT_HINT_BOUNCE_INTERVAL_MS) return
    startedRef.current = true

    void (async () => {
      // Ahead of the stamp, and it is the only guard that is (AGL-2691).
      //
      // The blob mint refuses an unverified session with a 403, so on
      // `/verify-email` — where every brand-new account lands and where this
      // component mounts like it does everywhere else — the bounce was spent
      // on a request that could not be answered. The stamp is written before
      // the fetch by design (the loop guard below), so the cost was not the
      // wasted POST: it was that the editor verified their address a minute
      // later and then got no `.aglyn.app` hint for the REST OF THE DAY. The
      // sign-out branch above clears the stamp precisely so the next sign-in
      // re-plants promptly; a guaranteed refusal must not undo that.
      //
      // A session that COULD have minted and did not still holds the stamp —
      // one silent miss, never a retry loop. That distinction is the whole
      // reason this sits here rather than beside the response check.
      if (await emailGateWouldRefuse(user)) return
      try {
        // BEFORE the navigation, deliberately: a bounce that fails after this
        // point costs one silent miss until the next window, never a loop.
        window.localStorage.setItem(
          EDIT_HINT_BOUNCE_STAMP_KEY,
          String(Date.now()),
        )
      } catch {
        // Unwritable stamp: bounce nothing, or every console load becomes a
        // redirect.
        return
      }
      try {
        const response = await authorizedFetch(user, '/api/edit-hint/blob', {
          method: 'POST',
        })
        if (!response.ok) return
        const payload = (await response.json()) as { blob?: string }
        if (!payload?.blob) return
        const url =
          `${EDIT_HINT_BOUNCE_ORIGIN}/api/edit-hint/set` +
          `?sig=${encodeURIComponent(payload.blob)}` +
          `&return=${encodeURIComponent(window.location.href)}`
        // Re-read at the last moment (AGL-3690). The check at the top ran
        // before two awaits, and the console home may have started
        // provisioning a sign-up's workspace since. Leaving now would strand
        // that account with no workspace. Skip this one and unstamp, so the
        // next console load plants the hint instead.
        if (isSignUpLandingHeld()) {
          try {
            window.localStorage.removeItem(EDIT_HINT_BOUNCE_STAMP_KEY)
          } catch {
            // A stamp that cannot be cleared costs one missed day, no more.
          }
          return
        }
        // Marked in the same tick as the check above, so a provisioning that
        // has not claimed yet sees this and waits for the return (AGL-3690).
        markLeavingConsole()
        const go = navigate ?? ((target: string) => window.location.assign(target))
        go(url)
      } catch {
        // Silent by design; the next throttle window retries.
      }
    })()
  }, [user, navigate, signUpLanding])

  return null
}
